import { useEffect, useState, useRef } from "react";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { compressImage } from "../lib/compressImage";
import { DatePickerField } from "../components/DatePickerField";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, Upload, FileText, Trash2, Send, Download, Mail, Plus, CheckCircle2, XCircle, AlertTriangle, Pencil, X, Repeat, Eye, Users, ChevronDown, ChevronRight, Folder, FolderPlus,
} from "lucide-react";

const FolderIcon = () => <Folder className="w-3.5 h-3.5 text-amber-600" />;
// Iter122ar · ModuliStudio spostato in Bacheca

const CATS = ["Amministrazione", "Contabilità", "Tecnico", "Impianti", "Sicurezza", "Energia"];
const RICORRENZE = [[12, "Ogni anno"], [24, "Ogni 2 anni"], [36, "Ogni 3 anni"], [60, "Ogni 5 anni"]];
const SEM = {
  verde: { cls: "bg-green-100 text-green-800 border-green-300", dot: "bg-green-500", txt: "Fascicolo completo", Icon: CheckCircle2 },
  giallo: { cls: "bg-amber-100 text-amber-800 border-amber-300", dot: "bg-amber-500", txt: "Documenti in scadenza", Icon: AlertTriangle },
  rosso: { cls: "bg-red-100 text-red-800 border-red-300", dot: "bg-red-500", txt: "Documenti mancanti o scaduti", Icon: XCircle },
};

export default function Ras() {
  const [condomini, setCondomini] = useState([]);
  const [cid, setCid] = useState("");
  const [fascicolo, setFascicolo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fornitori, setFornitori] = useState([]);
  const [condominiContatti, setCondominiContatti] = useState([]);
  const [destinatariPicker, setDestinatariPicker] = useState(null);
  const [richiesta, setRichiesta] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newDoc, setNewDoc] = useState({ categoria: "Amministrazione", documento: "", obbligatorio: false });
  const [addMassivo, setAddMassivo] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("fascicolo");
  const uploadRef = useRef({});
  const [dragOverId, setDragOverId] = useState(null);
  // Iter122ip · collasso elenco allegati quando > 1 file (uno stato per documento)
  const [expandedFiles, setExpandedFiles] = useState(() => new Set());
  const toggleFilesOpen = (id) => setExpandedFiles((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });


  useEffect(() => {
    api.get("/condomini").then((r) => {
      const arr = Array.isArray(r.data) ? r.data : r.data.condomini || [];
      setCondomini(arr);
      if (arr[0]) setCid(arr[0].id);
    });
    api.get("/fornitori", { params: { limit: 5000 } }).then((r) => setFornitori(r.data.fornitori || [])).catch(() => {});
  }, []);

  const load = (id) => {
    if (!id) return;
    setLoading(true);
    api.get(`/ras/condominio/${id}`).then((r) => setFascicolo(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Errore"))
      .finally(() => setLoading(false));
    // Iter122h · carica anche l'elenco condòmini con email per il picker "destinatari interni"
    api.get(`/condomini/${id}/contatti`)
      .then((r) => setCondominiContatti(Array.isArray(r.data) ? r.data : (r.data?.contatti || [])))
      .catch(() => setCondominiContatti([]));
  };
  useEffect(() => { if (cid) load(cid); }, [cid]);

  const updateDoc = async (doc, patch) => {
    try {
      await api.put(`/ras/documento/${doc.id}`, patch);
      // Feedback: se sto attivando visibilità ai condomini ma non c'è file
      if (patch.visibile_condomini === true && !doc.file_id) {
        toast.warning("Flag attivo, ma nessun file caricato: il condomino non vedrà il documento finché non carichi un file.", { duration: 5000 });
      } else if (patch.visibile_condomini === true) {
        toast.success("Documento reso visibile ai condòmini");
      }
      load(cid);
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const onUpload = async (doc, fileList, sottocategoria = "") => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    const fd = new FormData();
    const compressed = await Promise.all(arr.map((f) => compressImage(f)));
    compressed.forEach((f) => fd.append("files", f));
    if (sottocategoria) fd.append("sottocategoria", sottocategoria);
    try {
      const { data } = await api.post(`/ras/documento/${doc.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.count} document${data.count === 1 ? "o caricato" : "i caricati"}${sottocategoria ? ` in "${sottocategoria}"` : ""}`);
      load(cid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Caricamento non riuscito"); }
  };

  // Iter122ix · Cartelle (sottocategorie) all'interno di un documento RAS
  const createSottocategoria = async (doc) => {
    const raw = window.prompt(`Nuova cartella per "${doc.documento}" (es. 2026, 2027):`);
    const name = (raw || "").trim();
    if (!name) return;
    try {
      await api.post(`/ras/documento/${doc.id}/sottocategoria`, { nome: name });
      toast.success(`Cartella "${name}" creata`);
      load(cid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const deleteSottocategoria = async (doc, name) => {
    if (!window.confirm(`Eliminare la cartella "${name}"? Deve essere vuota.`)) return;
    try {
      await api.delete(`/ras/documento/${doc.id}/sottocategoria/${encodeURIComponent(name)}`);
      toast.success(`Cartella "${name}" eliminata`);
      load(cid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };
  const uploadToFolder = (doc, name) => {
    const el = document.createElement("input");
    el.type = "file"; el.multiple = true;
    el.accept = ".pdf,image/*,.doc,.docx";
    el.onchange = () => { if (el.files?.length) onUpload(doc, el.files, name); };
    el.click();
  };

  const removeFile = async (doc, fileId) => {
    if (!window.confirm("Rimuovere questo file dagli allegati?")) return;
    try { await api.delete(`/ras/documento/${doc.id}/file/${fileId}`); load(cid); }
    catch { toast.error("Errore"); }
  };
  const removeAllFiles = async (doc) => {
    if (!window.confirm(`Rimuovere tutti gli allegati (${(doc.files || []).length}) da "${doc.documento}"?`)) return;
    try { await api.delete(`/ras/documento/${doc.id}/file`); load(cid); } catch { toast.error("Errore"); }
  };
  const removeDoc = async (doc) => {
    if (!window.confirm(`Eliminare la voce "${doc.documento}" dal fascicolo di questo stabile?`)) return;
    try { await api.delete(`/ras/documento/${doc.id}`); load(cid); } catch { toast.error("Errore"); }
  };

  const addDoc = async () => {
    if (!newDoc.documento.trim()) { toast.error("Indica il nome del documento"); return; }
    try {
      if (addMassivo) {
        const { data } = await api.post("/ras/documento-massivo", newDoc);
        toast.success(`Documento aggiunto a ${data.aggiunti} condomìni · saltati ${data.saltati} (già presente)`);
        setAddOpen(false); setAddMassivo(false);
        setNewDoc({ categoria: "Amministrazione", documento: "", obbligatorio: false });
        if (cid) load(cid);
      } else {
        await api.post(`/ras/condominio/${cid}/documento`, newDoc);
        setAddOpen(false);
        setNewDoc({ categoria: "Amministrazione", documento: "", obbligatorio: false });
        load(cid);
      }
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const ripristinaCatalogoRas = async () => {
    if (!window.confirm("Ripristinare il catalogo RAS standard su tutti i condomìni che ne sono privi?\n\nVerranno aggiunte SOLO le voci mancanti (non tocca i documenti già presenti né le voci personalizzate). Utile a valle del bug post-deploy.")) return;
    try {
      const { data } = await api.post("/ras/ripristina-catalogo");
      toast.success(`Catalogo ripristinato · ${data.condomini_toccati} condomìni · ${data.totale_voci_aggiunte} voci aggiunte`);
      if (cid) load(cid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Ripristino non riuscito"); }
  };

  const inviaRichiesta = async (fornitore_id, messaggio) => {
    try {
      const { data } = await api.post(`/ras/documento/${richiesta.id}/richiedi-fornitore`, { fornitore_id, messaggio });
      toast.success(`Richiesta inviata a ${data.email}`); setRichiesta(null); load(cid);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
  };

  const scaricaReport = () => openSecureFile(`/ras/condominio/${cid}/report/pdf?inline=true`);
  const inviaReport = async () => {
    const email = window.prompt("Email destinatario (lascia vuoto per l'email dello Studio):", "");
    if (email === null) return;
    try { const { data } = await api.post(`/ras/condominio/${cid}/report/email`, { email }); toast.success(`Report inviato a ${data.email}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const conf = fascicolo?.conformita;
  const sem = conf ? SEM[conf.stato] : null;

  return (
    <div className="space-y-6" data-testid="ras-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold flex items-center gap-2">
          <ShieldCheck className="w-9 h-9 text-brand" /> RAS · Fascicolo di sicurezza
        </h1>
        <div className="flex items-center gap-2">
          {tab === "fascicolo" && (
            <select data-testid="ras-condominio-select" value={cid} onChange={(e) => setCid(e.target.value)}
              className="h-11 border-2 border-slate-300 rounded-lg px-3 bg-white text-sm min-w-[200px]">
              {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Iter122ar · rimosso tab "Moduli e compilazioni": la funzionalità è ora in Bacheca. */}

      {tab === "fascicolo" && sem && (
        <Card className={`p-4 border-2 ${sem.cls}`} data-testid="ras-conformita">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-3 font-bold">
              <span className={`w-4 h-4 rounded-full ${sem.dot} shrink-0`} />
              <sem.Icon className="w-5 h-5 shrink-0" /> <span>{sem.txt}</span>
            </div>
            <div className="text-sm font-semibold">
              Presenti {conf.presenti}/{conf.totale} · Obbligatori mancanti: {conf.mancanti_obbligatori} · Scaduti: {conf.scaduti} · In scadenza: {conf.in_scadenza}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={scaricaReport} data-testid="ras-report-pdf" className="h-9 bg-white"><Download className="w-4 h-4 mr-1" /> Report PDF</Button>
              <Button size="sm" variant="outline" onClick={inviaReport} data-testid="ras-report-email" className="h-9 bg-white"><Mail className="w-4 h-4 mr-1" /> Invia</Button>
              <Button size="sm" onClick={() => setAddOpen(true)} data-testid="ras-add-doc" className="h-9 bg-brand hover:bg-brand-dark text-white"><Plus className="w-4 h-4 mr-1" /> Documento</Button>
              <Button size="sm" variant="outline" onClick={ripristinaCatalogoRas} data-testid="ras-ripristina-catalogo" className="h-9 border-amber-400 text-amber-800 bg-white" title="Ripristina il catalogo standard su tutti i condomìni">
                Ripristina catalogo
              </Button>
            </div>
          </div>
        </Card>
      )}

      {tab === "fascicolo" && loading && <div className="text-slate-500">Caricamento…</div>}

      {tab === "fascicolo" && fascicolo && fascicolo.documenti.length > 0 && (
        <div className="text-xs text-slate-500 italic flex items-center gap-1.5" data-testid="ras-dnd-hint">
          <Upload className="w-3.5 h-3.5" /> Suggerimento: puoi trascinare più file direttamente sulla card di ogni documento per caricarli in un colpo solo.
        </div>
      )}

      {tab === "fascicolo" && fascicolo && CATS.filter((cat) => fascicolo.documenti.some((d) => d.categoria === cat)).map((cat) => (
        <Card key={cat} className="p-4 border-2" data-testid={`ras-cat-${cat}`}>
          <h2 className="font-display text-xl font-bold text-brand mb-3">{cat}</h2>
          <div className="space-y-2">
            {fascicolo.documenti.filter((d) => d.categoria === cat).map((d) => (
              <div key={d.id}
                data-testid={`ras-doc-${d.id}`}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (dragOverId !== d.id) setDragOverId(d.id); }}
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(d.id); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (!e.currentTarget.contains(e.relatedTarget)) setDragOverId(null); }}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  setDragOverId(null);
                  const files = Array.from(e.dataTransfer?.files || []);
                  if (files.length === 0) return;
                  onUpload(d, files);
                }}
                className={`relative p-3 rounded-lg border transition-all ${dragOverId === d.id ? "border-brand border-2 bg-brand/5 shadow-md" : "border-slate-100 hover:border-[#C98AA0]"}`}>
                {dragOverId === d.id && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-brand/10 z-10">
                    <div className="flex items-center gap-2 text-brand font-bold text-sm bg-white/95 px-3 py-1.5 rounded-full border-2 border-brand shadow-md">
                      <Upload className="w-4 h-4" /> Rilascia per caricare
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  {d.presente ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-1" /> : <XCircle className="w-4 h-4 text-slate-300 shrink-0 mt-1" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                      <span className="break-words">{d.documento}</span>
                      {d.obbligatorio && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-brand/10 text-brand shrink-0">OBBL.</span>}
                      {d.ricorrenza_mesi ? (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 shrink-0 inline-flex items-center gap-1" data-testid={`ras-ricorrente-badge-${d.id}`}>
                          <Repeat className="w-3 h-3" /> ogni {d.ricorrenza_mesi} mesi
                        </span>
                      ) : null}
                      {(d.files || []).length > 1 && (
                        <button type="button"
                          onClick={() => toggleFilesOpen(d.id)}
                          data-testid={`ras-count-${d.id}`}
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 shrink-0 inline-flex items-center gap-1 hover:bg-slate-200 transition"
                          title={expandedFiles.has(d.id) ? "Nascondi elenco allegati" : "Mostra elenco allegati"}>
                          {expandedFiles.has(d.id) ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                          {d.files.length} allegati
                        </button>
                      )}
                      {d.solleciti_count > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 shrink-0" data-testid={`ras-solleciti-badge-${d.id}`}>
                          {d.solleciti_count} sollecit{d.solleciti_count === 1 ? "o" : "i"}
                        </span>
                      )}
                    </div>
                    {(d.data_scadenza || d.fornitore_incaricato_nome || (d.condomini_destinatari_ids || []).length > 0) && (
                      <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                        {d.data_scadenza && <span>Scadenza: {d.data_scadenza}</span>}
                        {d.fornitore_incaricato_nome && <span>Incaricato: {d.fornitore_incaricato_nome}</span>}
                        {(d.condomini_destinatari_ids || []).length > 0 && (
                          <span data-testid={`ras-destinatari-info-${d.id}`}>
                            Destinatari interni: {d.condomini_destinatari_nomi || `${d.condomini_destinatari_ids.length} condòmini`}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Iter122ix · Raggruppamento in cartelle (sottocategorie), es. "2026","2027" */}
                    {(d.files || []).length > 0 && ((d.files.length <= 1) || expandedFiles.has(d.id)) && (() => {
                      const folders = Array.from(new Set([
                        ...((d.sottocategorie) || []),
                        ...((d.files || []).map((f) => (f.sottocategoria || "").trim()).filter(Boolean)),
                      ]));
                      folders.sort((a, b) => b.localeCompare(a)); // 2027 > 2026
                      const senza = (d.files || []).filter((f) => !((f.sottocategoria || "").trim()));
                      return (
                        <div className="mt-1.5 space-y-2" data-testid={`ras-files-${d.id}`}>
                          {folders.map((folder) => {
                            const key = `${d.id}::${folder}`;
                            const open = expandedFiles.has(key);
                            const items = (d.files || []).filter((f) => (f.sottocategoria || "").trim() === folder);
                            return (
                              <div key={folder} className="border border-slate-200 rounded-lg bg-slate-50/40" data-testid={`ras-folder-${d.id}-${folder}`}>
                                <div className="flex items-center gap-2 px-2 py-1.5">
                                  <button type="button" onClick={() => toggleFilesOpen(key)}
                                    className="text-xs font-bold text-slate-700 inline-flex items-center gap-1 hover:text-brand"
                                    data-testid={`ras-folder-toggle-${d.id}-${folder}`}>
                                    {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                    <FolderIcon /> {folder}
                                    <span className="text-slate-500 font-normal">({items.length})</span>
                                  </button>
                                  <div className="ml-auto flex items-center gap-1">
                                    <button type="button" className="text-xs text-slate-600 hover:text-brand"
                                      title={`Aggiungi file in "${folder}"`}
                                      onClick={() => uploadToFolder(d, folder)}
                                      data-testid={`ras-folder-upload-${d.id}-${folder}`}>
                                      <Upload className="w-3.5 h-3.5" />
                                    </button>
                                    {items.length === 0 && (
                                      <button type="button" className="text-xs text-red-600 hover:text-red-800"
                                        title="Elimina cartella vuota" onClick={() => deleteSottocategoria(d, folder)}
                                        data-testid={`ras-folder-del-${d.id}-${folder}`}>
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                {open && items.length > 0 && (
                                  <div className="px-2 pb-2 space-y-1">
                                    {items.map((f) => (
                                      <div key={f.file_id} className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1" data-testid={`ras-file-item-${f.file_id}`}>
                                        <FileText className="w-3.5 h-3.5 text-brand shrink-0" />
                                        <button type="button" className="text-green-700 font-medium hover:underline truncate min-w-0 flex-1 text-left"
                                          onClick={() => openSecureFile(`/files/${f.file_id}`)} title="Apri" data-testid={`ras-file-view-${f.file_id}`}>
                                          {f.file_name}
                                        </button>
                                        <button type="button" className="text-amber-700 hover:text-amber-800 shrink-0" title="Rimuovi"
                                          onClick={() => removeFile(d, f.file_id)} data-testid={`ras-file-del-${f.file_id}`}>
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {senza.length > 0 && (
                            <div className="space-y-1" data-testid={`ras-files-nocat-${d.id}`}>
                              {folders.length > 0 && (
                                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 px-1">Senza cartella</div>
                              )}
                              {senza.map((f) => (
                                <div key={f.file_id} className="flex items-center gap-2 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid={`ras-file-item-${f.file_id}`}>
                                  <FileText className="w-3.5 h-3.5 text-brand shrink-0" />
                                  <button type="button" className="text-green-700 font-medium hover:underline truncate min-w-0 flex-1 text-left"
                                    onClick={() => openSecureFile(`/files/${f.file_id}`)} title="Apri" data-testid={`ras-file-view-${f.file_id}`}>
                                    {f.file_name}
                                  </button>
                                  <button type="button" className="text-amber-700 hover:text-amber-800 shrink-0" title="Rimuovi"
                                    onClick={() => removeFile(d, f.file_id)} data-testid={`ras-file-del-${f.file_id}`}>
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {/* Cartelle vuote (create e senza file) — anche se non ci sono files, se ci sono sottocategorie mostra le cartelle */}
                    {(d.files || []).length === 0 && ((d.sottocategorie) || []).length > 0 && (
                      <div className="mt-1.5 space-y-1" data-testid={`ras-empty-folders-${d.id}`}>
                        {((d.sottocategorie) || []).map((folder) => (
                          <div key={folder} className="border border-dashed border-slate-300 rounded-lg bg-slate-50/40 px-2 py-1.5 flex items-center gap-2 text-xs">
                            <FolderIcon /> <span className="font-bold text-slate-700">{folder}</span>
                            <span className="text-slate-400">(vuota)</span>
                            <div className="ml-auto flex items-center gap-1">
                              <button type="button" className="text-slate-600 hover:text-brand"
                                onClick={() => uploadToFolder(d, folder)}
                                title="Aggiungi file" data-testid={`ras-folder-upload-empty-${d.id}-${folder}`}>
                                <Upload className="w-3.5 h-3.5" />
                              </button>
                              <button type="button" className="text-red-600 hover:text-red-800"
                                onClick={() => deleteSottocategoria(d, folder)}
                                title="Elimina cartella" data-testid={`ras-folder-del-empty-${d.id}-${folder}`}>
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2 pl-6">
                  <div className="w-full sm:w-52">
                    <DatePickerField testid={`ras-scad-${d.id}`} value={d.data_scadenza || ""} valueFormat="it"
                      placeholder="Scadenza" className="h-9 text-xs px-2" short
                      onChange={(v) => { if (v !== (d.data_scadenza || "")) updateDoc(d, { data_scadenza: v }); }} />
                  </div>
                  <div className="flex flex-wrap gap-2 sm:ml-auto">
                    <input type="file" hidden multiple ref={(el) => (uploadRef.current[d.id] = el)}
                      onChange={(e) => { onUpload(d, e.target.files); e.target.value = ""; }} accept=".pdf,image/*,.doc,.docx" />
                    <Button size="sm" variant="outline" className="h-9" title="Carica uno o più file" onClick={() => uploadRef.current[d.id]?.click()} data-testid={`ras-upload-${d.id}`}><Upload className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" className="h-9" title="Crea nuova cartella (es. per anno)" onClick={() => createSottocategoria(d)} data-testid={`ras-newfolder-${d.id}`}><FolderPlus className="w-4 h-4" /></Button>
                    <Button size="sm" variant="outline" className="h-9" title="Richiedi al fornitore" onClick={() => setRichiesta(d)} data-testid={`ras-request-${d.id}`}><Send className="w-4 h-4" /></Button>
                    {(d.files || []).length > 0 && <Button size="sm" variant="outline" className="h-9 text-amber-700" title="Rimuovi tutti gli allegati" onClick={() => removeAllFiles(d)} data-testid={`ras-delfile-${d.id}`}><X className="w-4 h-4" /></Button>}
                    <Button size="sm" variant="outline" className="h-9 text-red-700" title="Elimina voce" onClick={() => removeDoc(d)} data-testid={`ras-deldoc-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2 pl-6">
                  <select value={d.ricorrenza_mesi || ""} data-testid={`ras-ricorrenza-${d.id}`}
                    onChange={(e) => updateDoc(d, { ricorrenza_mesi: Number(e.target.value) || 0 })}
                    className="h-9 text-xs border-2 border-slate-200 rounded-lg px-2 bg-white">
                    <option value="">Non ricorrente</option>
                    {RICORRENZE.map(([m, l]) => <option key={m} value={m}>{l}</option>)}
                  </select>
                  {d.ricorrenza_mesi ? (
                    <>
                      <span className="text-xs text-slate-500">Preavviso</span>
                      <Input type="number" min="1" className="h-9 w-20 text-xs" defaultValue={d.preavviso_giorni || 30} data-testid={`ras-preavviso-${d.id}`}
                        onBlur={(e) => { const v = Number(e.target.value) || 30; if (v !== (d.preavviso_giorni || 30)) updateDoc(d, { preavviso_giorni: v }); }} />
                      <span className="text-xs text-slate-500">gg</span>
                      <select value={d.fornitore_incaricato_id || ""} data-testid={`ras-incaricato-${d.id}`}
                        onChange={(e) => updateDoc(d, { fornitore_incaricato_id: e.target.value })}
                        className="h-9 text-xs border-2 border-slate-200 rounded-lg px-2 bg-white max-w-[240px]">
                        <option value="">— Fornitore incaricato —</option>
                        {fornitori.filter((f) => f.email).map((f) => <option key={f.id} value={f.id}>{f.ragione_sociale}</option>)}
                      </select>
                      {/* Iter122h · Destinatari interni (condòmini) per attività eseguita internamente */}
                      <Button size="sm" variant="outline" type="button"
                        onClick={() => setDestinatariPicker(d)}
                        data-testid={`ras-destinatari-open-${d.id}`}
                        className="h-9 text-xs border-2 border-slate-200 bg-white gap-1.5 max-w-[280px] justify-start">
                        <Users className="w-3.5 h-3.5 text-brand shrink-0" />
                        <span className="truncate">
                          {(d.condomini_destinatari_ids || []).length > 0
                            ? `Destinatari interni: ${d.condomini_destinatari_ids.length}`
                            : "— Destinatari interni —"}
                        </span>
                      </Button>
                    </>
                  ) : null}
                  <label className={`flex items-center gap-1.5 text-xs font-semibold cursor-pointer sm:ml-auto ${d.visibile_condomini ? "text-brand" : "text-slate-500"}`}
                    title="Se attivo, i condòmini di questo stabile vedono il documento nella loro sezione 'Documenti e moduli'. È necessario che sia stato caricato almeno un file.">
                    <input type="checkbox" className="w-4 h-4 accent-brand" checked={!!d.visibile_condomini}
                      onChange={(e) => updateDoc(d, { visibile_condomini: e.target.checked })} data-testid={`ras-visibile-${d.id}`} />
                    <Eye className="w-3.5 h-3.5" /> Visibile ai condòmini
                  </label>
                  {d.visibile_condomini && !d.file_id && (
                    <span data-testid={`ras-visibile-warning-${d.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800"
                      title="Il flag è attivo ma non è stato caricato alcun file: il condomino non vedrà questo documento finché non viene caricato.">
                      ⚠ Nessun file caricato
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}

      {/* Iter122jw · La sezione "Numeri utili" è ora gestita in Bacheca → Numeri utili (non più in RAS). */}

      {/* Iter122h · Dialog scelta destinatari interni (condòmini) */}
      <Dialog open={!!destinatariPicker} onOpenChange={(o) => !o && setDestinatariPicker(null)}>
        <DialogContent className="max-w-lg" data-testid="ras-destinatari-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Users className="w-5 h-5 text-brand" /> Destinatari interni
            </DialogTitle>
          </DialogHeader>
          {destinatariPicker && (
            <DestinatariInterniForm
              doc={destinatariPicker}
              condomini={condominiContatti}
              onSave={async (ids) => {
                try {
                  await api.put(`/ras/documento/${destinatariPicker.id}`, { condomini_destinatari_ids: ids });
                  toast.success(ids.length > 0 ? `Destinatari aggiornati (${ids.length})` : "Destinatari interni rimossi");
                  setDestinatariPicker(null);
                  load(cid);
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
              }}
              onCancel={() => setDestinatariPicker(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog richiesta fornitore */}
      <Dialog open={!!richiesta} onOpenChange={(o) => !o && setRichiesta(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl flex items-center gap-2"><Send className="w-5 h-5 text-brand" /> Richiedi al fornitore</DialogTitle></DialogHeader>
          {richiesta && <RichiestaForm doc={richiesta} fornitori={fornitori} onSend={inviaRichiesta} />}
        </DialogContent>
      </Dialog>

      {/* Dialog aggiungi documento */}
      <Dialog open={addOpen} onOpenChange={(v) => { setAddOpen(v); if (!v) setAddMassivo(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl">Aggiungi documento al fascicolo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-semibold">Categoria</Label>
              <select data-testid="ras-new-categoria" value={newDoc.categoria} onChange={(e) => setNewDoc({ ...newDoc, categoria: e.target.value })}
                className="h-11 mt-1 w-full border-2 border-slate-300 rounded-lg px-3 bg-white">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm font-semibold">Nome documento</Label>
              <Input data-testid="ras-new-documento" className="h-11 mt-1" value={newDoc.documento} onChange={(e) => setNewDoc({ ...newDoc, documento: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="w-4 h-4 accent-brand" data-testid="ras-new-obbl" checked={newDoc.obbligatorio} onChange={(e) => setNewDoc({ ...newDoc, obbligatorio: e.target.checked })} />
              <span className="text-sm">Documento obbligatorio</span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg bg-amber-50 border border-amber-200">
              <input type="checkbox" className="w-4 h-4 mt-0.5 accent-amber-600" data-testid="ras-new-massivo" checked={addMassivo} onChange={(e) => setAddMassivo(e.target.checked)} />
              <span className="text-sm text-amber-800"><b>Aggiungi a tutti i condomìni</b><br /><span className="text-xs">La voce sarà creata in ogni fascicolo. I condomìni che l'hanno già la salteranno automaticamente.</span></span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Annulla</Button>
            <Button data-testid="ras-new-save" onClick={addDoc} className="bg-brand hover:bg-brand-dark">
              {addMassivo ? "Aggiungi a tutti" : "Aggiungi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RichiestaForm({ doc, fornitori, onSend }) {
  const [fid, setFid] = useState("");
  const [msg, setMsg] = useState("");
  const conEmail = fornitori.filter((f) => f.email);
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">Documento richiesto: <b>{doc.documento}</b>. Il fornitore riceverà un'email con un link per caricarlo senza accedere.</p>
      <div>
        <Label className="text-sm font-semibold">Fornitore (con email)</Label>
        <select data-testid="ras-request-fornitore" value={fid} onChange={(e) => setFid(e.target.value)}
          className="h-11 mt-1 w-full border-2 border-slate-300 rounded-lg px-3 bg-white">
          <option value="">— seleziona —</option>
          {conEmail.map((f) => <option key={f.id} value={f.id}>{f.ragione_sociale} ({f.email})</option>)}
        </select>
      </div>
      <div>
        <Label className="text-sm font-semibold">Messaggio (facoltativo)</Label>
        <textarea data-testid="ras-request-msg" rows={3} className="w-full mt-1 border-2 border-slate-300 rounded-lg px-3 py-2 text-sm" value={msg} onChange={(e) => setMsg(e.target.value)} />
      </div>
      <Button data-testid="ras-request-send" disabled={!fid} onClick={() => onSend(fid, msg)} className="w-full h-11 bg-brand hover:bg-brand-dark font-bold"><Send className="w-4 h-4 mr-2" /> Invia richiesta</Button>
    </div>
  );
}


function DestinatariInterniForm({ doc, condomini, onSave, onCancel }) {
  const [selected, setSelected] = useState(() => new Set(doc.condomini_destinatari_ids || []));
  const [q, setQ] = useState("");
  // Solo soggetti con email valida (requisito: destinatario riceve promemoria via email + in-app)
  const conEmail = (condomini || []).filter((c) => (c.email || "").trim());
  const filtered = conEmail.filter((c) => {
    if (!q.trim()) return true;
    const s = `${c.nome || ""} ${c.email || ""} ${c.unita || ""}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  });
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectAllFiltered = () => {
    const next = new Set(selected);
    filtered.forEach((c) => next.add(c.id));
    setSelected(next);
  };
  const clearAll = () => setSelected(new Set());
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Attività ricorrente eseguita <b>internamente</b>: seleziona uno o più condòmini che riceveranno
        il promemoria informativo (email + notifica in app) all'avvicinarsi della scadenza. Il caricamento
        del documento resterà sempre gestito dallo Studio.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Cerca nome, email, unità…" value={q} onChange={(e) => setQ(e.target.value)}
          className="h-9 text-sm flex-1 min-w-[200px]" data-testid="ras-destinatari-search" />
        <Button type="button" size="sm" variant="outline" onClick={selectAllFiltered}
          data-testid="ras-destinatari-selall" className="h-9 text-xs">Seleziona visibili</Button>
        <Button type="button" size="sm" variant="outline" onClick={clearAll}
          data-testid="ras-destinatari-clear" className="h-9 text-xs">Deseleziona tutti</Button>
      </div>
      <div className="max-h-72 overflow-y-auto border-2 border-slate-200 rounded-lg divide-y"
        data-testid="ras-destinatari-list">
        {filtered.length === 0 && (
          <div className="p-3 text-sm text-slate-500 italic">Nessun condòmino con email disponibile.</div>
        )}
        {filtered.map((c) => (
          <label key={c.id} className="flex items-start gap-2 p-2 hover:bg-slate-50 cursor-pointer"
            data-testid={`ras-destinatario-row-${c.id}`}>
            <input type="checkbox" className="w-4 h-4 mt-1 accent-brand"
              checked={selected.has(c.id)} onChange={() => toggle(c.id)}
              data-testid={`ras-destinatario-check-${c.id}`} />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm truncate">{c.nome}</div>
              <div className="text-xs text-slate-500 truncate">
                {c.email}{c.unita ? ` · Unità ${c.unita}` : ""}{c.ruolo ? ` · ${c.ruolo}` : ""}
              </div>
            </div>
          </label>
        ))}
      </div>
      <div className="text-xs text-slate-500" data-testid="ras-destinatari-count">
        Selezionati: <b>{selected.size}</b> · Disponibili con email: {conEmail.length}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="ras-destinatari-cancel">Annulla</Button>
        <Button type="button" onClick={() => onSave(Array.from(selected))}
          data-testid="ras-destinatari-save" className="bg-brand hover:bg-brand-dark">Salva</Button>
      </DialogFooter>
    </div>
  );
}
